import React, { useCallback } from "react";
import { connect } from "react-redux";

import Col from "antd/es/col";
import Row from "antd/es/row";
import Select from "antd/es/select";
import Switch from "antd/es/switch";
import { isBefore, startOfToday } from "date-fns/esm";
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router-dom";

import FrequencyType from "@mapmycustomers/shared/enum/FrequencyType";

import DatePicker from "@app/component/input/DatePicker";
import TextWithLine from "@app/component/typography/TextWithLine";
import localSettings from "@app/config/LocalSettings";
import OrganizationSetting from "@app/enum/OrganizationSetting";
import Path from "@app/enum/Path";
import SettingPath from "@app/enum/settings/SettingPath";
import TimeZones from "@app/enum/TimeZones";
import { getOrganizationSettingValue } from "@app/store/iam";
import {
  changeFrequency,
  changeStartSyncDate,
  toggleEmailSyncState,
} from "@app/store/integrations/actions";
import {
  getCurrentIntegration,
  getCurrentIntegrationFrequency,
  getCurrentIntegrationNextRunAt,
  getIntegrationEmailSyncStats,
} from "@app/store/integrations/selectors";
import { RootState } from "@app/store/rootReducer";
import Integration from "@app/types/integrations/Integration";
import TimeZone from "@app/types/TimeZone";
import { SHOULD_USE_12_HOUR_FORMAT } from "@app/util/consts";
import getFrequencyTypeDisplayName from "@app/util/getFrequencyTypeDisplayName";
import getDefaultScheduleDate from "@app/util/integrations/getDefaultScheduleDate";
import { parseApiDateWithTz } from "@app/util/parsers";

import { getIntegrationAnalyticsSetupIssuer } from "../../integrationAnalytics";

import styles from "./Steps.module.scss";
import StepWrapper from "./StepWrapper";

const { Option } = Select;

const disabledDate = (current: Date, organizationTimezone: string) =>
  current && isBefore(current, parseApiDateWithTz(startOfToday(), organizationTimezone));

interface Props {
  changeFrequency: typeof changeFrequency;
  changeStartSyncDate: typeof changeStartSyncDate;
  currentIntegration?: Integration;
  emailSyncStats?: boolean;
  frequency?: FrequencyType;
  organizationTimezone?: string;
  serviceName: string;
  startSyncDate?: Date;
  toggleEmailSyncState: typeof toggleEmailSyncState;
}

const Step3: React.FC<Props> = ({
  changeFrequency,
  changeStartSyncDate,
  currentIntegration,
  emailSyncStats,
  frequency = FrequencyType.DAILY,
  organizationTimezone,
  serviceName,
  startSyncDate = parseApiDateWithTz(getDefaultScheduleDate(), organizationTimezone),
  toggleEmailSyncState,
}) => {
  const intl = useIntl();

  const handleFrequencyChange = useCallback(
    (frequency) => {
      changeFrequency(frequency);
      getIntegrationAnalyticsSetupIssuer(currentIntegration?.type, 3).clicked([
        "Change Frequency",
        frequency,
      ]);
    },
    [changeFrequency, currentIntegration]
  );

  const isDisabledDate = useCallback(
    (current: Date) => disabledDate(current, organizationTimezone ?? "UTC"),
    [organizationTimezone]
  );

  const handleChangeDate = useCallback(
    (nextRunAt: Date | null) => {
      if (!nextRunAt) {
        return;
      }
      const scheduleTime = parseApiDateWithTz(getDefaultScheduleDate(), organizationTimezone);
      const date = isBefore(nextRunAt, parseApiDateWithTz(Date.now(), organizationTimezone))
        ? scheduleTime
        : nextRunAt;
      changeStartSyncDate(date);
      getIntegrationAnalyticsSetupIssuer(currentIntegration?.type, 3).clicked(
        ["Change Sync Begin Date"],
        { date }
      );
    },
    [currentIntegration?.type, changeStartSyncDate, organizationTimezone]
  );

  const handleToggleEmailSyncState = useCallback(() => {
    getIntegrationAnalyticsSetupIssuer(currentIntegration?.type, 3).clicked([
      `Toggle Receive Emails ${emailSyncStats ? "Off" : "On"}`,
    ]);
    toggleEmailSyncState();
  }, [currentIntegration?.type, emailSyncStats, toggleEmailSyncState]);

  return (
    <StepWrapper>
      <div>
        <Row>
          <Col sm={{ span: 20 }} xl={{ span: 16 }}>
            <TextWithLine className={styles.textColor}>
              <FormattedMessage
                id="integrations.syncFrequencyStep.selectFrequencyDescription"
                defaultMessage="How frequently should records sync from {serviceName} to Map My Customers?"
                description="Description of frequency selection"
                values={{ serviceName }}
              />
            </TextWithLine>
            <Select
              className={styles.halfWidth}
              onChange={handleFrequencyChange}
              placeholder={intl.formatMessage({
                id: "integrations.syncFrequencyStep.selectFrequency",
                defaultMessage: "When should your sync begin?",
                description: "Title of a Select frequency placeholder",
              })}
              value={frequency}
            >
              {(localSettings.wasSkeletonKeyUsed() || frequency === FrequencyType.HOURLY) && (
                <Option value={FrequencyType.HOURLY}>
                  {getFrequencyTypeDisplayName(intl, FrequencyType.HOURLY)}
                </Option>
              )}
              <Option value={FrequencyType.DAILY}>
                {getFrequencyTypeDisplayName(intl, FrequencyType.DAILY)}
              </Option>
              <Option value={FrequencyType.WEEKLY}>
                {getFrequencyTypeDisplayName(intl, FrequencyType.WEEKLY)}
              </Option>
              <Option value={FrequencyType.MONTHLY}>
                {getFrequencyTypeDisplayName(intl, FrequencyType.MONTHLY)}
              </Option>
            </Select>
          </Col>
        </Row>

        <Row className={styles.scheduleRow}>
          <Col sm={{ span: 20 }} xl={{ span: 16 }}>
            <TextWithLine className={styles.textColor}>
              <FormattedMessage
                id="integrations.syncFrequencyStep.chooseDay"
                defaultMessage="When should your sync begin?"
                description="Choose day for syncing"
              />
            </TextWithLine>
            <div className={styles.timeDescription}>
              {intl.formatMessage(
                {
                  id: "integrations.syncFrequencyStep.timeDescription",
                  defaultMessage:
                    "Syncs will be scheduled based on your <link>organization timezone</link> which is currently <b>{organizationTimezone}</b>",
                  description: "Syncing time description",
                },
                {
                  b: (text) => <b>{text}</b>,
                  link: (text) => (
                    <Link
                      target="_blank"
                      to={`${Path.SETTINGS}/${SettingPath.ORGANIZATION_DETAILS}`}
                    >
                      {text}
                    </Link>
                  ),
                  organizationTimezone: intl.formatMessage(
                    TimeZones[organizationTimezone as TimeZone]
                  ),
                }
              )}
            </div>
            <div className={styles.datetimeContainer}>
              <DatePicker
                allowClear={false}
                className={styles.halfWidth}
                disabledDate={isDisabledDate}
                format={`yyyy-MM-dd ${SHOULD_USE_12_HOUR_FORMAT ? "hh:mm a" : "HH:mm"}`}
                onChange={handleChangeDate}
                onSelect={handleChangeDate}
                placeholder={intl.formatMessage({
                  id: "integrations.syncFrequencyStep.selectDesiredSyncDay",
                  defaultMessage: "Select your desired sync day",
                  description: "Placeholder of select day datepicker",
                })}
                showHour
                showMinute
                showSecond={false}
                showTime
                use12Hours={SHOULD_USE_12_HOUR_FORMAT}
                value={startSyncDate}
              />
            </div>
          </Col>
        </Row>
        <Row className={styles.scheduleRow}>
          <Col sm={{ span: 20 }} xl={{ span: 16 }}>
            <TextWithLine className={styles.textColor}>
              <Row justify="space-between">
                <FormattedMessage
                  id="integrations.syncFrequencyStep.receiveEmails"
                  defaultMessage="Receive emails with integration details after each sync?"
                  description="Receive emails option"
                />
                <Switch
                  checked={emailSyncStats}
                  className={styles.receiveEmailsSwitch}
                  onChange={handleToggleEmailSyncState}
                />
              </Row>
            </TextWithLine>
          </Col>
        </Row>
      </div>
    </StepWrapper>
  );
};

const mapStateToProps = (state: RootState) => ({
  currentIntegration: getCurrentIntegration(state),
  emailSyncStats: getIntegrationEmailSyncStats(state),
  frequency: getCurrentIntegrationFrequency(state),
  organizationTimezone: getOrganizationSettingValue(state)(OrganizationSetting.TIMEZONE),
  startSyncDate: getCurrentIntegrationNextRunAt(state),
});

const mapDispatchToProps = {
  changeFrequency,
  changeStartSyncDate,
  toggleEmailSyncState,
};

export default connect(mapStateToProps, mapDispatchToProps)(Step3);
